import { useEffect, useState } from "react";
import {
  Box,
  ChevronLeft,
  ChevronRight,
  Flex,
  Icon,
  Spinner,
} from "@storyofams/react-ui";
import { useRouter } from "next/router";
import qs from "qs";
import { useQuery } from "react-query";

import styled from "styled-components";
import { css } from "styled-components";
import { Container } from "~components/common/Layout/Container";
import { FiltersBar } from "~components/search/FiltersBar";
import { ProductResults } from "~components/search/ProductResults";
import { shopifySdk } from "~lib/shopify/client";

interface PageInfo {
  first?: number;
  last?: number;
  before?: string;
  after?: string;
}

const urlToSearchState = (path) => qs.parse(path?.split("?")?.[1]);
const bannedFilter = "filter.p.m.eigenschappen.hide";

export const Results = () => {
  const router = useRouter();
  const collectionHandle = router.query.handle
    ? String(router.query.handle)
    : "all-collections";
  const [filtersState, setFiltersState] = useState([]);
  const [pageInfo, setPageInfo] = useState<PageInfo>({ first: 12 });
  const [searchState, setSearchState] = useState(
    urlToSearchState(router.asPath)
  );

  useEffect(() => {
    const nextSearchState = urlToSearchState(router.asPath);
    if (JSON.stringify(searchState) !== JSON.stringify(nextSearchState)) {
      setPageInfo({ first: 12 });
      setSearchState(nextSearchState);
    }
  }, [router.asPath]);
  const { data: searchResult, isLoading } = useQuery(
    ["searchProducts", searchState, pageInfo],
    async () => {
      const filters = Object.entries(searchState)
        .filter((item) => item[1])
        .map(([key, value]) => {
          const clearKey = key.replace("amp;", "");
          const values = String(value).split("+");
          if (clearKey === "soort_product")
            return {
              productMetafield: {
                namespace: "eigenschappen",
                key: "soort_product",
                value: String(value),
              },
            };
          if (clearKey === "product_type")
            return {
              productType: String(value),
            };
          return values.map((item) => ({
            productMetafield: {
              namespace: "custom",
              key: clearKey,
              value: item,
            },
          }));
        })
        .flat(2);
      const result = await shopifySdk.collection({
        ...pageInfo,
        handle: collectionHandle,
        reverse:
          searchState.reverse === "true" ||
          searchState["amp;reverse"] === "true"
            ? true
            : false,
        sortKey: searchState.sorting
          ? String(searchState.sorting)
          : "COLLECTION_DEFAULT",
        productFilters: [
          {
            productMetafield: {
              namespace: "eigenschappen",
              key: "hide",
              value: "false",
            },
          },
          ...filters,
        ],
      });
      const filteredResult = result.collectionByHandle.products.edges
        .map((item) => item.node)
        .filter((item) => {
          const availableVariants = item.variants.edges.filter(
            ({ node }) => node?.hide?.value !== "true"
          );
          return availableVariants.some(({ node }) => node.compareAtPriceV2);
        });
      const newFilters =
        result?.collectionByHandle.products.filters?.filter(
          ({ id }) => id !== bannedFilter
        ) || [];
      setFiltersState(newFilters);
      return {
        hits:
          collectionHandle === "sale"
            ? filteredResult || []
            : result.collectionByHandle.products.edges.map(
                (item) => item.node
              ) || [],
        hasNextPage:
          result?.collectionByHandle?.products?.pageInfo?.hasNextPage,
        hasPreviousPage:
          result?.collectionByHandle?.products?.pageInfo?.hasPreviousPage,
        startCursor:
          result?.collectionByHandle?.products?.pageInfo?.startCursor,
        endCursor: result?.collectionByHandle?.products?.pageInfo?.endCursor,
      };
    },
    {
      refetchOnWindowFocus: false,
    }
  );
  const onPaginationClickHandler = (type: string) => {
    scrollToReviewsSection();
    if (type === "forward")
      setPageInfo({
        first: 12,
        after: searchResult.endCursor,
      });
    else
      setPageInfo({
        last: 12,
        before: searchResult.startCursor,
      });
  };
  const scrollToReviewsSection = (cb = null) => {
    const el = document?.getElementById("description");

    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };
  return (
    <>
      <FiltersBar productFilters={filtersState} />
      <Container withOverflowHidden pt={[5, 7]}>
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <ProductResults isCollection={true} searchResult={searchResult} />
            <Box display={"flex"} width={"100%"} justifyContent={"center"}>
              <StyledButton
                onClick={() =>
                  searchResult?.hasPreviousPage
                    ? onPaginationClickHandler("back")
                    : false
                }
                border="1px solid grey900"
                as={!searchResult?.hasPreviousPage ? "div" : "button"}
                opacity={!searchResult?.hasPreviousPage && 0.5}
              >
                <Icon icon={ChevronLeft} width="10px" />
              </StyledButton>

              <StyledButton
                ml={2}
                onClick={() =>
                  searchResult.hasNextPage
                    ? onPaginationClickHandler("forward")
                    : false
                }
                border="1px solid grey900"
                as={!searchResult?.hasNextPage ? "div" : "button"}
                opacity={!searchResult?.hasNextPage && 0.5}
              >
                <Icon icon={ChevronRight} width="10px" />
              </StyledButton>
            </Box>
          </>
        )}
      </Container>
    </>
  );
};
const StyledButton = styled(Flex)<{ isActive?: boolean }>`
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  font-size: 12px;
  border-radius: 50%;
  color: ${(p) => p.theme.colors.grey900};

  ${(p) =>
    p.isActive &&
    css`
      color: ${(p) => p.theme.colors.grey100};
      background-color: ${(p) => p.theme.colors.grey900};
    `}
`;
